// hooks/useFirestore.js
import { useState, useEffect, useCallback } from 'react';
import { FirestoreService, handleFirestoreError } from '../services/firestore';

export function useFirestore(collectionName) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [service] = useState(() => new FirestoreService(collectionName));

  // Clear error
  const clearError = useCallback(() => setError(null), []);

  // Get all documents
  const getAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const documents = await service.getAll();
      setData(documents);
      return documents;
    } catch (err) {
      const errorMessage = handleFirestoreError(err);
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [service]);

  // Create document
  const create = useCallback(async (documentData) => {
    setLoading(true);
    setError(null);
    try {
      const newDoc = await service.create(documentData);
      setData(prev => [...prev, newDoc]);
      return newDoc;
    } catch (err) {
      const errorMessage = handleFirestoreError(err);
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [service]);

  // Update document
  const update = useCallback(async (id, updateData) => {
    setLoading(true);
    setError(null);
    try {
      const updatedDoc = await service.update(id, updateData);
      setData(prev => prev.map(item => item.id === id ? updatedDoc : item));
      return updatedDoc;
    } catch (err) {
      const errorMessage = handleFirestoreError(err);
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [service]);

  // Delete document
  const remove = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      await service.delete(id);
      setData(prev => prev.filter(item => item.id !== id));
      return true;
    } catch (err) {
      const errorMessage = handleFirestoreError(err);
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [service]);

  // Get document by ID
  const getById = useCallback(async (id) => {
    setError(null);
    try {
      return await service.getById(id);
    } catch (err) {
      const errorMessage = handleFirestoreError(err);
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, [service]);

  // Query documents
  const query = useCallback(async (conditions = [], orderByField = null, orderDirection = 'asc') => {
    setLoading(true);
    setError(null);
    try {
      const documents = await service.query(conditions, orderByField, orderDirection);
      return documents;
    } catch (err) {
      const errorMessage = handleFirestoreError(err);
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [service]);

  // Subscribe to real-time updates
  const subscribe = useCallback((conditions = [], orderByField = null) => {
    setError(null);
    return service.subscribe(
      (documents, subscriptionError) => {
        if (subscriptionError) {
          const errorMessage = handleFirestoreError(subscriptionError);
          setError(errorMessage);
        } else {
          setData(documents);
        }
      },
      conditions,
      orderByField
    );
  }, [service]);

  return {
    data,
    loading,
    error,
    getAll,
    create,
    update,
    remove,
    getById,
    query,
    subscribe,
    clearError,
    service
  };
}